/**
 * Queue Processor Button - Temporary component for testing RAG
 * Add this to your app to easily process the embedding queue
 */

import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { useToast } from '@/hooks/use-toast';
import { processEmbeddingQueue, getQueueStats } from '@/lib/process-embedding-queue';
import { getEmbeddingStats } from '@/lib/rag-service';
import { backfillEmbeddings, checkMissingEmbeddings } from '@/lib/backfill-embeddings';
import { useAuth } from '@/contexts/AuthContext';
import { Loader2, Play, RefreshCw, Database, AlertCircle } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';

export function QueueProcessorButton() {
  const [processing, setProcessing] = useState(false);
  const [backfilling, setBackfilling] = useState(false);
  const [queueStats, setQueueStats] = useState<any>(null);
  const [embeddingStats, setEmbeddingStats] = useState<any>(null);
  const [missingEmbeddings, setMissingEmbeddings] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const { toast } = useToast();
  const { profile } = useAuth();

  const loadStats = async () => {
    setLoading(true);
    try {
      const qStats = await getQueueStats();
      setQueueStats(qStats);

      if (profile?.company_id) {
        const eStats = await getEmbeddingStats(profile.company_id);
        setEmbeddingStats(eStats);
        
        // Check for missing embeddings
        const missing = await checkMissingEmbeddings(profile.company_id);
        setMissingEmbeddings(missing);
      }
    } catch (error: any) {
      console.error('Error loading stats:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadStats();
  }, [profile?.company_id]);

  const handleProcessQueue = async () => {
    if (!queueStats?.unprocessed || queueStats.unprocessed === 0) {
      toast({
        title: 'Queue Empty',
        description: 'No items to process in the queue',
      });
      return;
    }

    setProcessing(true);
    try {
      console.log('🔄 Processing queue...');
      
      const result = await processEmbeddingQueue(10, (progress) => {
        console.log(`Progress: ${progress.processed}/${progress.total} (✓${progress.succeeded} ✗${progress.failed})`);
      });

      console.log('✅ Queue processing complete:', result);

      toast({
        title: 'Queue Processed',
        description: `Successfully processed ${result.succeeded} policies. ${result.failed > 0 ? `${result.failed} failed.` : ''}`,
        variant: result.failed > 0 ? 'destructive' : 'default'
      });

      if (result.failed > 0) {
        console.error('Failed items:', result.errors);
      }

      // Reload stats
      await loadStats();
    } catch (error: any) {
      console.error('❌ Queue processing error:', error);
      toast({
        title: 'Processing Failed',
        description: error.message || 'Failed to process queue',
        variant: 'destructive'
      });
    } finally {
      setProcessing(false);
    }
  };

  const handleBackfillEmbeddings = async () => {
    if (!profile?.company_id) {
      toast({
        title: 'Error',
        description: 'Company ID not found',
        variant: 'destructive'
      });
      return;
    }

    if (!missingEmbeddings || missingEmbeddings.missing === 0) {
      toast({
        title: 'All Policies Embedded',
        description: 'All approved policies already have embeddings',
      });
      return;
    }

    setBackfilling(true);
    try {
      console.log('🔄 Backfilling embeddings...');
      
      const result = await backfillEmbeddings(profile.company_id, (progress) => {
        console.log(`Progress: ${progress.processed}/${progress.total} (✓${progress.succeeded} ✗${progress.failed})`);
      });

      console.log('✅ Backfill complete:', result);

      toast({
        title: 'Backfill Complete',
        description: `Successfully generated embeddings for ${result.succeeded} policies. ${result.failed > 0 ? `${result.failed} failed.` : ''}`,
        variant: result.failed > 0 ? 'destructive' : 'default'
      });

      if (result.failed > 0 && result.errors.length > 0) {
        console.error('Failed policies:', result.errors);
      }

      // Reload stats
      await loadStats();
    } catch (error: any) {
      console.error('❌ Backfill error:', error);
      toast({
        title: 'Backfill Failed',
        description: error.message || 'Failed to backfill embeddings',
        variant: 'destructive'
      });
    } finally {
      setBackfilling(false);
    }
  };

  return (
    <Card className="w-full max-w-2xl">
      <CardHeader>
        <CardTitle className="flex items-center justify-between">
          <span>RAG Embedding Queue</span>
          <Button
            variant="outline"
            size="sm"
            onClick={loadStats}
            disabled={loading}
          >
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          </Button>
        </CardTitle>
        <CardDescription>
          Process pending policy embeddings for RAG search
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Queue Stats */}
        <div className="grid grid-cols-3 gap-4">
          <div className="space-y-1">
            <p className="text-sm font-medium text-muted-foreground">Unprocessed</p>
            <p className="text-2xl font-bold">{queueStats?.unprocessed || 0}</p>
          </div>
          <div className="space-y-1">
            <p className="text-sm font-medium text-muted-foreground">Succeeded</p>
            <p className="text-2xl font-bold text-green-600">{queueStats?.succeeded || 0}</p>
          </div>
          <div className="space-y-1">
            <p className="text-sm font-medium text-muted-foreground">Failed</p>
            <p className="text-2xl font-bold text-red-600">{queueStats?.failed || 0}</p>
          </div>
        </div>

        {/* Embedding Stats */}
        {embeddingStats && (
          <div className="grid grid-cols-3 gap-4 pt-4 border-t">
            <div className="space-y-1">
              <p className="text-sm font-medium text-muted-foreground">Embedded Policies</p>
              <p className="text-2xl font-bold">{embeddingStats.totalPolicies}</p>
            </div>
            <div className="space-y-1">
              <p className="text-sm font-medium text-muted-foreground">Total Chunks</p>
              <p className="text-2xl font-bold">{embeddingStats.totalChunks}</p>
            </div>
            <div className="space-y-1">
              <p className="text-sm font-medium text-muted-foreground">Avg Chunks/Policy</p>
              <p className="text-2xl font-bold">{embeddingStats.avgChunksPerPolicy}</p>
            </div>
          </div>
        )}

        {/* Missing Embeddings Alert */}
        {missingEmbeddings && missingEmbeddings.missing > 0 && (
          <Alert>
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>
              {missingEmbeddings.missing} approved {missingEmbeddings.missing === 1 ? 'policy' : 'policies'} {missingEmbeddings.missing === 1 ? 'is' : 'are'} missing embeddings.
            </AlertDescription>
          </Alert>
        )}

        {/* Process Queue Button */}
        <Button
          onClick={handleProcessQueue}
          disabled={processing || backfilling || !queueStats?.unprocessed || queueStats.unprocessed === 0}
          className="w-full"
          size="lg"
          variant="default"
        >
          {processing ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Processing Queue...
            </>
          ) : (
            <>
              <Play className="mr-2 h-4 w-4" />
              Process Queue ({queueStats?.unprocessed || 0} items)
            </>
          )}
        </Button>

        {/* Backfill Button */}
        {missingEmbeddings && missingEmbeddings.missing > 0 && (
          <Button
            onClick={handleBackfillEmbeddings}
            disabled={processing || backfilling}
            className="w-full"
            size="lg"
            variant="outline"
          >
            {backfilling ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Generating Embeddings...
              </>
            ) : (
              <>
                <Database className="mr-2 h-4 w-4" />
                Generate Embeddings for {missingEmbeddings.missing} {missingEmbeddings.missing === 1 ? 'Policy' : 'Policies'}
              </>
            )}
          </Button>
        )}

        {/* Info Text */}
        <p className="text-xs text-muted-foreground text-center">
          Process queue handles pending items. Generate embeddings creates chunks for approved policies missing them. Check browser console for detailed logs.
        </p>
      </CardContent>
    </Card>
  );
}

